// @owners { team: patients-team }
import { BREAKPOINTS, COLORS, SIZES } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Avatar,
  Button,
  Card,
  Column,
  Icon,
  LgPadding,
  LoadingEmptyState,
  Row,
  Toast,
  ToastContext,
} from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { selectUser } from '~shared/actions/ui/users';
// eslint-disable-next-line import/no-deprecated
import { uploadImageToS3 } from '~shared/actions/upload';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers, updateUser } from '~shared/actions/users';
import getUser from '~shared/features/users/selectors/getUser';
import { getDisplayFullName } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type User } from '~shared/types';
import { MedicalInfo } from './MedicalInfo';
import { MedicalInfoActionSheet } from './MedicalInfoActionSheet';
import { PersonalInfo } from './PersonalInfo';
import { PersonalInfoActionSheet } from './PersonalInfoActionSheet';
import { ProfileInsurances } from './ProfileInsurances';
import { Breadcrumbs } from '~web/components/Breadcrumbs';
import { ClickableAltoIcon } from '~web/components/ClickableAltoIcon';
import { PageBackground } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';

type Props = {
  readonly user: User;
};

const PatientPhoto = styled.div<{ imgUrl?: string }>`
  ${({ imgUrl }) =>
    imgUrl &&
    `
    background-image: url(${imgUrl});
    background-size: 150% auto;
    background-position: center center;
    background-repeat: no-repeat;
    background-color: ${COLORS.PALETTE.GREYSCALE.DEFAULT};
  `}
  width: 80px;
  height: 80px;
  border-radius: 40px;
`;

const UploadUserPhoto = ({ user }: Pick<Props, 'user'>) => {
  const { addToast } = useContext(ToastContext);
  const dispatch = useDispatchShared();

  const handleUpdateAvatar = async (image: File) => {
    try {
      // eslint-disable-next-line import/no-deprecated
      const url = await dispatch(uploadImageToS3(image, 'users'));
      if (url && user) {
        dispatch(
          // eslint-disable-next-line import/no-deprecated
          updateUser({
            id: user.id,
            avatar_url: url,
          }),
        );
        // eslint-disable-next-line import/no-deprecated
        dispatch(fetchUsers());
      }
    } catch {
      addToast(<Toast variant="error">Something went wrong. Try uploading another photo.</Toast>);
    }
  };

  return (
    <Row center>
      <Dropzone
        multiple={false}
        onDrop={(filesToUpload) => {
          handleUpdateAvatar(filesToUpload[0]);
        }}
      >
        {({ getRootProps, getInputProps }) => (
          // @ts-expect-error Types of property backfaceVisibility are incompatible.
          <Row
            {...getRootProps()}
            bottom
          >
            <input {...getInputProps()} />
            {user.avatar_url ? (
              <PatientPhoto
                imgUrl={user.avatar_url}
                aria-label="Your uploaded photo"
              />
            ) : (
              <Avatar
                name={user.first_name ?? 'Patient'}
                initials={`${user.first_name?.charAt(0)?.toUpperCase()}${user.last_name?.charAt(0)?.toUpperCase()}`}
                size={SIZES.AVATAR.LG.value}
                backgroundColor={COLORS.BACKGROUND_COLORS.GREY}
              />
            )}
            <ClickableAltoIcon
              onClick={() => {
                /* do nothing; dropzone handles click */
              }}
              AltoIcon={
                <Icon
                  name="edit"
                  type="secondary"
                />
              }
            />
          </Row>
        )}
      </Dropzone>
    </Row>
  );
};

export const UserProfile = () => {
  const { userID: userIDString } = useParams<{ userID: string }>();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const dispatch = useDispatchShared();
  const userID: number | null = userIDString ? parseInt(userIDString, 10) || null : null;
  const user = useSelectorShared((state) =>
    getUser(state, {
      userID,
    }),
  );
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView({
      event: EVENTS.PROFILE__INSURANCE_INFO_VIEWED,
    });
  }, [trackPageView]);

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers());
  }, [dispatch]);

  if (!user) {
    return (
      <LgPadding>
        <LoadingEmptyState />
      </LgPadding>
    );
  }

  const name = getDisplayFullName(user);
  const breadcrumbs = [
    {
      text: 'Account',
      to: '/me',
    },
    {
      text: name || '',
    },
  ];

  return (
    <PageBackground maxWidth={`${BREAKPOINTS.SM}px`}>
      <Column gap="MD">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <PageHeader>{name || ''}</PageHeader>
        <UploadUserPhoto user={user} />
        <Card
          title="Personal Info"
          RightContent={
            <Button
              label="EDIT"
              type="tertiary"
              small
              accessibilityLabel="Press to edit personal info"
              onPress={() => {
                dispatch(selectUser(user.id));
                setActiveActionSheet(<PersonalInfoActionSheet />);
              }}
            />
          }
        >
          <PersonalInfo user={user} />
        </Card>
        <Card
          title="Medical Info"
          RightContent={
            <Button
              label="EDIT"
              type="tertiary"
              small
              accessibilityLabel="Press to edit medical info"
              onPress={() => {
                dispatch(selectUser(user.id));
                setActiveActionSheet(<MedicalInfoActionSheet />);
              }}
            />
          }
        >
          <MedicalInfo user={user} />
        </Card>
        <ProfileInsurances userID={user.id} />
      </Column>
    </PageBackground>
  );
};
